import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatInlineNotification } from "@cat-digital-blocks/react/bedrock/components/InlineNotification";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatDropdown } from "@cat-digital-blocks/react/bedrock/components/Dropdown";
import { CatList } from "@cat-digital-blocks/react/bedrock/components/List";
import { CatListItem } from "@cat-digital-blocks/react/bedrock/components/ListItem";
import { CatLoadingIndicator } from "@cat-digital-blocks/react/bedrock/components/LoadingIndicator";
import {
  useCreateDataSourceMappingMutation,
  useGetHeliosDataSourcesQuery,
} from "@services/dcmApi";
import type { PalTypeResponse, HeliosDataSourceResponse } from "@/types/api";
import styles from "./DataSourceRegistration.module.scss";

interface CreateMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  palTypes: PalTypeResponse[];
  userName?: string;
}

const CreateMappingModal = ({
  isOpen,
  onClose,
  onSuccess,
  palTypes,
  userName,
}: CreateMappingModalProps) => {
  const { t } = useTranslation();

  const [selectedDataSource, setSelectedDataSource] =
    useState<HeliosDataSourceResponse | null>(null);
  const [selectedPalType, setSelectedPalType] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Fetch Helios data sources from HELIOS_NEXUS database
  const {
    data: heliosDataSources,
    isLoading: isLoadingDataSources,
    isError: isDataSourcesError,
  } = useGetHeliosDataSourcesQuery(undefined, {
    skip: !isOpen,
  });

  const [createMapping, { isLoading }] = useCreateDataSourceMappingMutation();

  // Sort data sources alphabetically by display name
  const sortedDataSources = useMemo(() => {
    if (!heliosDataSources) return [];
    return [...heliosDataSources].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
  }, [heliosDataSources]);

  const handleSourceNameChange = useCallback(
    (event: CustomEvent) => {
      const selectedValue = event.detail?.value;
      const val =
        typeof selectedValue === "string"
          ? selectedValue
          : selectedValue?.value;
      if (val && heliosDataSources) {
        // Find the full data source object by displayName
        const dataSource = heliosDataSources.find(
          (ds) => ds.displayName === val,
        );
        if (dataSource) {
          setSelectedDataSource(dataSource);
          setError(null);
        }
      }
    },
    [heliosDataSources],
  );

  const handlePalTypeChange = useCallback((event: CustomEvent) => {
    const selectedValue = event.detail?.value;
    const val =
      typeof selectedValue === "string" ? selectedValue : selectedValue?.value;
    if (val) {
      setSelectedPalType(val);
      setError(null);
    }
  }, []);

  const validateForm = useCallback((): boolean => {
    if (!selectedDataSource) {
      setError(t("admin.dataSourceRegistration.validation.sourceNameRequired"));
      return false;
    }
    if (!selectedPalType) {
      setError(t("admin.dataSourceRegistration.validation.palTypeRequired"));
      return false;
    }
    return true;
  }, [selectedDataSource, selectedPalType, t]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm() || !selectedDataSource) return;
    try {
      await createMapping({
        databaseName: selectedDataSource.schemaName, // Database name from schema
        schemaName: selectedDataSource.schemaName,
        sourceName: selectedDataSource.viewName,
        palType: selectedPalType,
        userName,
      }).unwrap();
      onSuccess();
    } catch (err: unknown) {
      if (
        err &&
        typeof err === "object" &&
        "status" in err &&
        err.status === 409
      ) {
        setError(t("admin.dataSourceRegistration.error.duplicate"));
      } else {
        setError(t("admin.dataSourceRegistration.error.createFailed"));
      }
    }
  }, [
    validateForm,
    createMapping,
    selectedDataSource,
    selectedPalType,
    userName,
    onSuccess,
    t,
  ]);

  const handleClose = useCallback(() => {
    setSelectedDataSource(null);
    setSelectedPalType("");
    setError(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <CatHeading variant="title-sm">
            {t("admin.dataSourceRegistration.createModal.title")}
          </CatHeading>
        </div>
        <div className={styles.modalBody}>
          {error && (
            <CatInlineNotification
              variant="error"
              className={styles.errorMessage}
            >
              {error}
            </CatInlineNotification>
          )}

          {isDataSourcesError && (
            <CatInlineNotification
              variant="error"
              className={styles.errorMessage}
            >
              {t("admin.dataSourceRegistration.error.loadDataSourcesFailed")}
            </CatInlineNotification>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="sourceName">
              {t("admin.dataSourceRegistration.createModal.sourceName")} *
            </label>
            {isLoadingDataSources ? (
              <div className={styles.loadingContainer}>
                <CatLoadingIndicator small />
                <span>{t("common.loading")}</span>
              </div>
            ) : (
              <CatDropdown
                label=""
                size="sm"
                value={selectedDataSource?.displayName || ""}
                onBlSelect={handleSourceNameChange}
                disabled={isLoading || isLoadingDataSources}
                placeholder={t(
                  "admin.dataSourceRegistration.createModal.sourceNamePlaceholder",
                )}
              >
                <CatList spacing="padded">
                  {sortedDataSources.map((ds) => (
                    <CatListItem key={ds.displayName} value={ds.displayName}>
                      {ds.displayName}
                    </CatListItem>
                  ))}
                </CatList>
              </CatDropdown>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="palType">
              {t("admin.dataSourceRegistration.createModal.palType")} *
            </label>
            <CatDropdown
              label=""
              size="sm"
              value={selectedPalType}
              onBlSelect={handlePalTypeChange}
              disabled={isLoading}
              placeholder={t(
                "admin.dataSourceRegistration.createModal.palTypePlaceholder",
              )}
            >
              <CatList spacing="padded">
                {palTypes.map((pt) => (
                  <CatListItem key={pt.id} value={pt.typeName}>
                    {pt.typeName}
                  </CatListItem>
                ))}
              </CatList>
            </CatDropdown>
          </div>
        </div>
        <div className={styles.modalActions}>
          <CatButton
            variant="contrast"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </CatButton>
          <CatButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? t("common.saving") : t("common.save")}
          </CatButton>
        </div>
      </div>
    </div>
  );
};

export default CreateMappingModal;
